import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useRuntimeSetting } from 'shared/hooks';
import { usePriceCorrection } from 'shared/hooks/usePriceCorrection';
import { selectPrice } from 'shared/selectors/jsonQuery/selectPrice';
import { TModifier } from 'shared/types/filters/modifier';

interface IUseDailyRentPriceCorrectionResult {
  /** Признак что была коррекция (для показа подсказки) */
  showCorrectionHint: boolean;
  /** Обёртка для обработки изменения min цены */
  wrapMinChange: (value: number | null, onChange: (modifier: TModifier) => void) => void;
  /** Обёртка для обработки изменения max цены */
  wrapMaxChange: (value: number | null, onChange: (modifier: TModifier) => void) => void;
}

/**
 * Хук для коррекции цен с учётом минимального разбега.
 *
 * Возвращает wrapper-функции которые:
 * - Применяют коррекцию если разбег между min и max меньше минимального
 * - Устанавливают флаг showCorrectionHint при коррекци
 */
export const useDailyRentPriceCorrection = (): IUseDailyRentPriceCorrectionResult => {
  const { gte: priceMin, lte: priceMax } = useSelector(selectPrice);

  const pricesMinGap = useRuntimeSetting<number>('Dailyrent.PriceInputs.MinGap', 1000);
  const calculateCorrectedPrice = usePriceCorrection({ pricesMinGap });

  const [showCorrectionHint, setShowCorrectionHint] = useState(false);

  const wrapMinChange = useCallback(
    (value: number | null, onChange: (modifier: TModifier) => void) => {
      if (value !== null && priceMax !== undefined) {
        const { adjustedMin, adjustedMax, wasCorrected } = calculateCorrectedPrice({
          newMin: value,
          newMax: priceMax,
          isMinChanged: true,
        });

        if (wasCorrected) {
          setShowCorrectionHint(true);
          onChange({ action: 'setPrice', arguments: [adjustedMin, adjustedMax] });

          return;
        }
      }

      onChange({ action: 'setPriceMin', arguments: [value] });
    },
    [priceMax, calculateCorrectedPrice],
  );

  const wrapMaxChange = useCallback(
    (value: number | null, onChange: (modifier: TModifier) => void) => {
      if (value !== null) {
        const { adjustedMin, adjustedMax, wasCorrected } = calculateCorrectedPrice({
          newMin: priceMin ?? null,
          newMax: value,
          isMinChanged: false,
        });

        if (wasCorrected) {
          setShowCorrectionHint(true);
          onChange({ action: 'setPrice', arguments: [adjustedMin, adjustedMax] });

          return;
        }
      }

      onChange({ action: 'setPriceMax', arguments: [value] });
    },
    [priceMin, calculateCorrectedPrice],
  );

  return {
    showCorrectionHint,
    wrapMinChange,
    wrapMaxChange,
  };
};
